import { useEffect, useMemo, useRef, useState } from 'react';
import { rememberMedia } from '../lib/mediaMap';

// In-flight/first-page responses shared between the app bootstrap prefetch and the hook.
const prefetched = new Map();
// «Ціна від – до» (?price_min=…&price_max=…): whole hryvnias, ignored when malformed.
export function priceRangeOf(params) {
  const read = key => {
    const raw = params.get(key);
    if (raw == null || !/^\d{1,8}$/.test(raw)) return null;
    return Number(raw);
  };
  let min = read('price_min'), max = read('price_max');
  if (min != null && max != null && min > max) [min, max] = [max, min];
  return {min, max};
}
function addPriceRange(query, params) {
  const {min, max} = priceRangeOf(params);
  if (min != null) query.set('price_min', String(min));
  if (max != null) query.set('price_max', String(max));
}
// The card price for the current selection (server card_offers) travels with each product.
function withOffers(data) {
  const offers = data?.card_offers || {};
  return {...data, items: (data?.items || []).map(item => offers[item.slug] ? {...item, card_offer: offers[item.slug]} : item)};
}
const PREFETCH_TTL = 30000;
export function catalogQuery(search, categorySlug, sale = false) {
  const params = new URLSearchParams(search);
  const sort = ['popular', 'newest', 'price_asc', 'price_desc'].includes(params.get('sort')) ? params.get('sort') : 'popular';
  let filters = {};
  try {
    const parsed = JSON.parse(params.get('filters'));
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) filters = Object.fromEntries(Object.entries(parsed).filter(([, values]) => Array.isArray(values)).map(([key, values]) => [key, [...new Set(values.filter(value => typeof value === 'string'))]]).filter(([, values]) => values.length));
  } catch {}
  const query = new URLSearchParams({limit: '50', sort});
  if (categorySlug) query.set('category', categorySlug);
  if (sale) query.set('sale', 'true');
  if (Object.keys(filters).length) query.set('filters', JSON.stringify(filters));
  addPriceRange(query, params);
  return query.toString();
}
// Uses a startup prefetch once (if still fresh), otherwise a normal abortable request.
export function fetchCatalogPage(queryKey, page, signal) {
  const url = `/api/public/catalog?${queryKey}&page=${page}`;
  const hit = prefetched.get(url);
  if (hit) {
    prefetched.delete(url);
    if (Date.now() - hit.at < PREFETCH_TTL) return hit.promise;
  }
  return fetch(url, {signal}).then(response => {
    if (!response.ok) throw new Error('Catalog unavailable');
    return response.json();
  }).then(rememberMedia).then(withOffers);
}
function prefetchPage(queryKey, page) {
  const url = `/api/public/catalog?${queryKey}&page=${page}`;
  const promise = fetch(url).then(response => {
    if (!response.ok) throw new Error('Catalog unavailable');
    return response.json();
  }).then(rememberMedia).then(withOffers);
  prefetched.set(url, {at: Date.now(), promise});
  promise.catch(() => prefetched.delete(url));
}
// Called at startup for /shop, /sale and /category/:slug so products load in parallel with the app.
export function prefetchCatalogFor(pathname, search) {
  const category = pathname.match(/^\/category\/([^/?#]+)/);
  if (!category && pathname !== '/shop' && pathname !== '/sale') return;
  const page = Number(new URLSearchParams(search).get('page')) || 1;
  if (page !== 1) return;
  prefetchPage(catalogQuery(search, category ? decodeURIComponent(category[1]) : undefined, pathname === '/sale'), 1);
}

// view 'more' = pages 1..N accumulate («Завантажити ще»); 'pages' = only page N (numbered pages).
export function useCatalog(search, categorySlug, sale = false, view = 'more') {
  const params = new URLSearchParams(search);
  const pageValue = Number(params.get('page'));
  const page = Number.isSafeInteger(pageValue) && pageValue > 0 ? pageValue : 1;
  const sort = ['popular', 'newest', 'price_asc', 'price_desc'].includes(params.get('sort')) ? params.get('sort') : 'popular';
  const filtersParam = params.get('filters');
  const filters = useMemo(() => {
    try {
      const parsed = JSON.parse(filtersParam);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
      return Object.fromEntries(Object.entries(parsed).filter(([, values]) => Array.isArray(values)).map(([key, values]) => [key, [...new Set(values.filter(value => typeof value === 'string'))]]).filter(([, values]) => values.length));
    } catch { return {}; }
  }, [filtersParam]);
  const query = new URLSearchParams({limit: '50', sort});
  if (categorySlug) query.set('category', categorySlug);
  if (sale) query.set('sale', 'true');
  if (Object.keys(filters).length) query.set('filters', JSON.stringify(filters));
  addPriceRange(query, params);
  const priceRange = priceRangeOf(params);
  const queryKey = query.toString();
  const [attempt, setAttempt] = useState(0);
  const cache = useRef({key: '', pages: new Map()});
  const [state, setState] = useState({products: [], total: 0, pages: 0, filtersMeta: null, metaFor: null, isLoading: true, hasLoaded: false});
  // Filter meta (incl. «Ванни» photo tiles) belongs to the category it was loaded for. On the first render after
  // switching category the state still holds the previous category's meta — never hand it out for the new one.
  const scopeKey = `${categorySlug || ''}|${sale ? 1 : 0}`;
  useEffect(() => {
    const controller = new AbortController();
    const changed = cache.current.key !== queryKey;
    if (changed) cache.current = {key: queryKey, pages: new Map()};
    const entry = cache.current;
    // Same category: keep the filter panel (and its scroll position in the phone sheet) while the new page loads.
    setState(prev => ({...(changed ? {products: [], total: 0, pages: 0, filtersMeta: prev.metaFor === scopeKey ? prev.filtersMeta : null, metaFor: prev.metaFor === scopeKey ? scopeKey : null, hasLoaded: false} : prev), isLoading: true, error: null}));
    async function load() {
      let last;
      const unique = new Map();
      for (let current = view === 'pages' ? page : 1; current <= page; current += 1) {
        let data = entry.pages.get(current);
        if (!data) {
          data = await fetchCatalogPage(queryKey, current, controller.signal);
          if (controller.signal.aborted) return;
          entry.pages.set(current, data);
        }
        last = data;
        for (const product of data.items || []) unique.set(product.slug || product.id, product);
        if (current >= data.pages) break;
      }
      if (controller.signal.aborted) return;
      setState({products: [...unique.values()], total: last?.total || 0, pages: last?.pages || 0, filtersMeta: last?.filters_meta || null, metaFor: scopeKey, isLoading: false, hasLoaded: true});
    }
    load().catch(error => { if (!controller.signal.aborted) setState(prev => ({...prev, isLoading: false, error})); });
    return () => controller.abort();
  }, [queryKey, page, attempt, scopeKey, view]);
  return {...state, filtersMeta: state.metaFor === scopeKey ? state.filtersMeta : null, page, sort, filters, priceRange, retry: () => setAttempt(value => value + 1)};
}
